import styled, { keyframes } from "styled-components";

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const StyledHeader = styled.header`
  width: 100%;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &.scrolled {
    height: 80px;
    background: var(--header-bg-scrolled);
    backdrop-filter: blur(20px);
    box-shadow: 0 4px 30px var(--shadow-medium);

    .headerContainer {
      .imageLogo {
        height: 60px;
        width: auto;
      }
    }
  }

  .mobileHeader {
    display: none;
  }

  .headerContainer {
    display: flex;
    max-width: 1400px;
    height: 100%;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding: 0 40px;

    .imageLogo {
      height: 70px;
      width: auto;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        transform: scale(1.02);
      }
    }

    .headerItems {
      display: flex;
      align-items: center;
      gap: 12px;

      ul {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        padding: 0;
        list-style: none;
      }

      ul li {
        position: relative;

        a {
          display: block;
          padding: 12px 20px;
          text-decoration: none;
          color: var(--text-primary);
          font-family: Poppins;
          font-size: 15px;
          font-weight: 500;
          border-radius: 12px;
          transition: all 0.3s ease;
          position: relative;

          &::before {
            content: "";
            position: absolute;
            bottom: 8px;
            left: 20px;
            right: 20px;
            height: 2px;
            background: var(--accent);
            transform: scaleX(0);
            transition: transform 0.3s ease;
          }

          &:hover {
            color: var(--accent);

            &::before {
              transform: scaleX(1);
            }
          }
        }
      }

      .ctaButtonSecondary {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 24px;
        background: transparent;
        color: var(--text-primary);
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        border: 2px solid var(--card-border);
        border-radius: 12px;
        cursor: pointer;
        text-decoration: none;
        transition: all 0.3s ease;

        svg {
          width: 16px;
          height: 16px;
          transition: transform 0.3s ease;
        }

        &:hover {
          border-color: var(--primary);
          color: var(--primary);
          background: rgba(43, 59, 124, 0.1);
          transform: translateY(-2px);
        }
      }

      .ctaButton {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 28px;
        background: linear-gradient(135deg, var(--accent) 0%, #c12f2a 100%);
        color: #fff;
        font-family: Poppins;
        font-size: 14px;
        font-weight: 600;
        border: none;
        border-radius: 12px;
        cursor: pointer;
        text-decoration: none;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(217, 58, 53, 0.3);

        svg {
          width: 16px;
          height: 16px;
          transition: transform 0.3s ease;
        }

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(217, 58, 53, 0.4);

          svg {
            transform: translateX(3px);
          }
        }
      }

      .themeToggleWrapper {
        margin-left: 8px;
      }
    }
  }

  @media only screen and (max-width: 1024px) {
    .headerContainer {
      padding: 0 30px;

      .headerItems ul li a {
        padding: 10px 14px;
        font-size: 14px;
      }

      .headerItems .ctaButtonSecondary {
        padding: 10px 18px;
      }

      .headerItems .ctaButton {
        padding: 10px 20px;
      }
    }
  }

  @media only screen and (max-width: 768px) {
    height: 80px;

    &.scrolled {
      height: 70px;
    }

    .mobileHeader {
      display: flex;
      align-items: center;
      gap: 16px;
      padding-right: 20px;
    }

    .headerContainer {
      padding: 0 20px;

      .imageLogo {
        height: 50px;
      }

      .headerItems {
        display: none;
      }
    }
  }
`;

export const MobileMenuOverlay = styled.div<{ open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9998;
  opacity: ${({ open }) => (open ? 1 : 0)};
  visibility: ${({ open }) => (open ? "visible" : "hidden")};
  transition: all 0.3s ease;
`;

export const MobileMenu = styled.nav<{ open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 320px;
  max-width: 85vw;
  height: 100vh;
  background: var(--card-bg);
  z-index: 9999;
  transform: ${({ open }) => (open ? "translateX(0)" : "translateX(-100%)")};
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  .menuHeader {
    padding: 24px;
    border-bottom: 1px solid var(--card-border);
    display: flex;
    justify-content: space-between;
    align-items: center;

    .menuLogo {
      height: 45px;
      width: auto;
    }

    .closeBtn {
      width: 44px;
      height: 44px;
      border-radius: 12px;
      border: none;
      background: var(--bg-secondary);
      color: var(--text-primary);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;

      svg {
        width: 24px;
        height: 24px;
      }

      &:hover {
        background: var(--accent);
        color: #fff;
      }
    }
  }

  .menuItems {
    flex: 1;
    padding: 24px;

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    li {
      margin-bottom: 8px;
      animation: ${fadeIn} 0.4s ease forwards;
      opacity: 0;

      &:nth-child(1) { animation-delay: 0.1s; }
      &:nth-child(2) { animation-delay: 0.15s; }
      &:nth-child(3) { animation-delay: 0.2s; }
      &:nth-child(4) { animation-delay: 0.25s; }
      &:nth-child(5) { animation-delay: 0.3s; }

      a {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 16px 20px;
        text-decoration: none;
        color: var(--text-primary);
        font-family: Poppins;
        font-size: 16px;
        font-weight: 500;
        border-radius: 14px;
        transition: all 0.3s ease;

        .menuIcon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          background: linear-gradient(
            135deg,
            rgba(217, 58, 53, 0.1) 0%,
            rgba(43, 59, 124, 0.1) 100%
          );
          display: flex;
          align-items: center;
          justify-content: center;

          svg {
            width: 20px;
            height: 20px;
            color: var(--accent);
          }
        }

        &:hover {
          background: var(--bg-secondary);
          color: var(--accent);
        }
      }
    }
  }

  .menuFooter {
    padding: 24px;
    border-top: 1px solid var(--card-border);

    .menuCtaSecondary {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      width: 100%;
      padding: 16px 24px;
      background: transparent;
      color: var(--text-primary);
      font-family: Poppins;
      font-size: 15px;
      font-weight: 600;
      border: 2px solid var(--card-border);
      border-radius: 14px;
      cursor: pointer;
      text-decoration: none;
      transition: all 0.3s ease;
      margin-bottom: 12px;

      svg {
        width: 18px;
        height: 18px;
      }

      &:hover {
        border-color: var(--primary);
        color: var(--primary);
        background: rgba(43, 59, 124, 0.1);
      }
    }

    .menuCta {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      width: 100%;
      padding: 16px 24px;
      background: linear-gradient(135deg, var(--accent) 0%, #c12f2a 100%);
      color: #fff;
      font-family: Poppins;
      font-size: 15px;
      font-weight: 600;
      border: none;
      border-radius: 14px;
      cursor: pointer;
      text-decoration: none;
      transition: all 0.3s ease;
      margin-bottom: 16px;

      svg {
        width: 18px;
        height: 18px;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 30px rgba(217, 58, 53, 0.3);
      }
    }

    .themeRow {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 0;

      span {
        font-family: Poppins;
        font-size: 14px;
        color: var(--text-secondary);
      }
    }
  }
`;

export const BurgerButton = styled.button<{ open: boolean }>`
  width: 44px;
  height: 44px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  background: var(--card-bg);
  border: 2px solid var(--card-border);
  border-radius: 12px;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--accent);
  }

  span {
    display: block;
    width: 20px;
    height: 2px;
    background: var(--text-primary);
    border-radius: 2px;
    transition: all 0.3s ease;
    transform-origin: center;

    &:first-child {
      transform: ${({ open }) =>
        open ? "rotate(45deg) translateY(8px)" : "rotate(0)"};
    }

    &:nth-child(2) {
      opacity: ${({ open }) => (open ? 0 : 1)};
      transform: ${({ open }) => (open ? "translateX(-10px)" : "translateX(0)")};
    }

    &:last-child {
      transform: ${({ open }) =>
        open ? "rotate(-45deg) translateY(-8px)" : "rotate(0)"};
    }
  }
`;
